import { analysisClient } from "@/shared/lib/supabase";
import type {
  LinkedVariableOption,
  NormalizedTemplateFilterOptions,
  NormalizedVariableFilterOptions,
  PaletteStepWithSettings,
  StepWithSettings,
  TemplateListItem,
  TemplateWithSettings,
  Variable,
} from "./types";

export async function fetchTemplates(
  options: NormalizedTemplateFilterOptions,
  currentTenantId: string,
): Promise<TemplateListItem[]> {
  const pageSize = 1000;
  const rows: TemplateWithSettings[] = [];
  for (let offset = 0; ; offset += pageSize) {
    let query = analysisClient
      .from("template")
      .select(
        "*, template_setting!inner(settings, display_name, display_description)",
      );
    if (options.search.length > 0) {
      query = query.filter(
        "template_setting.display_name",
        "ilike",
        `%${options.search}%`,
      );
    }
    const { data, error } = await query
      .order("record_created", { ascending: false })
      .order("analysis_id", { ascending: false })
      .range(offset, offset + pageSize - 1);
    if (error) throw error;
    rows.push(...((data ?? []) as TemplateWithSettings[]));
    if ((data?.length ?? 0) < pageSize) break;
  }

  const visibleTemplates = options.showGlobal
    ? rows
    : rows.filter((template) => template.record_tenant_id === currentTenantId);

  return visibleTemplates.map((template) => ({
    template: template as TemplateWithSettings,
    isGlobal: template.record_tenant_id !== currentTenantId,
  }));
}

export async function fetchTemplateById(
  id: string,
): Promise<TemplateWithSettings> {
  const [templateResult, variablesResult] = await Promise.all([
    analysisClient
      .from("template")
      .select(
        "*, template_setting(settings, display_name, display_description)",
      )
      .eq("analysis_id", id)
      .single(),
    analysisClient
      .from("variable")
      .select("*")
      .eq("analysis_id", id)
      .is("analysis_step_id", null)
      .order("variable_order", { ascending: true }),
  ]);
  if (templateResult.error) throw templateResult.error;
  if (variablesResult.error) throw variablesResult.error;
  return {
    ...(templateResult.data as TemplateWithSettings),
    variables: (variablesResult.data ?? []) as Variable[],
  };
}

export async function fetchTemplateSections(
  templateId: string,
): Promise<StepWithSettings[]> {
  const { data, error } = await analysisClient
    .from("templatestep")
    .select(
      "*, templatestep_setting(settings, display_name, display_description, display_output_types)",
    )
    .eq("analysis_id", templateId)
    .order("analysis_order", { ascending: true });
  if (error) throw error;
  const steps = (data ?? []) as StepWithSettings[];
  const stepIds = steps.map((step) => step.analysis_step_id);
  if (stepIds.length === 0) return [];
  const variablesResult = await analysisClient
    .from("variable")
    .select("*")
    .in("analysis_step_id", stepIds)
    .order("variable_order", { ascending: true });
  if (variablesResult.error) throw variablesResult.error;
  const variables = (variablesResult.data ?? []) as Variable[];
  return steps.map((step) => ({
    ...step,
    variables: variables.filter(
      (variable) => variable.analysis_step_id === step.analysis_step_id,
    ),
  }));
}

export async function fetchTemplatePaletteSteps(
  templateIds: string[],
): Promise<PaletteStepWithSettings[]> {
  const { data, error } = await analysisClient
    .from("templatestep")
    .select(
      "analysis_id, analysis_step_id, analysis_step_name, analysis_order, templatestep_setting(settings, display_name, display_description, display_output_types)",
    )
    .in("analysis_id", templateIds)
    .order("analysis_id", { ascending: true })
    .order("analysis_order", { ascending: true });

  if (error) throw error;
  const steps = (data ?? []) as PaletteStepWithSettings[];
  if (!steps.length) return [];
  const bindings = await analysisClient
    .from("variable")
    .select("analysis_step_id")
    .in(
      "analysis_step_id",
      steps.map((step) => step.analysis_step_id),
    )
    .neq("variable_role", "ordinary");
  if (bindings.error) throw bindings.error;
  const reserved = new Set(
    (bindings.data ?? []).map((row) => row.analysis_step_id),
  );
  return steps.filter((step) => !reserved.has(step.analysis_step_id));
}

export async function fetchVariables(
  options: NormalizedVariableFilterOptions,
  currentTenantId?: string,
): Promise<Variable[]> {
  let query = analysisClient
    .from("variable")
    .select("*, children:variable!parent_variable_id(variable_id)")
    .is("analysis_id", null)
    .is("analysis_step_id", null);

  query =
    options.parentVariableId === null
      ? query.is("parent_variable_id", null)
      : query.eq("parent_variable_id", options.parentVariableId);

  const { data, error } = await query
    .order("variable_group", { ascending: true, nullsFirst: false })
    .order("variable_order", { ascending: true })
    .order("variable_name", { ascending: true });

  if (error) throw error;
  type VariableReadRow = Variable & {
    children?: Array<{ variable_id: string }>;
  };
  const rows = (data ?? []) as unknown as VariableReadRow[];
  return rows
    .filter(
      (row) =>
        options.includeGlobal ||
        (Boolean(currentTenantId) && row.record_tenant_id === currentTenantId),
    )
    .map(({ children, ...row }) => ({
      ...row,
      has_children: (children?.length ?? 0) > 0,
    }));
}

export async function fetchAnalysisVariables(
  analysisId: string,
): Promise<Variable[]> {
  if (!analysisId) return [];

  const { data, error } = await analysisClient
    .from("variable")
    .select("*")
    .eq("analysis_id", analysisId)
    .is("analysis_step_id", null)
    .order("variable_order", { ascending: true })
    .order("variable_name", { ascending: true });

  if (error) throw error;
  const variables = (data ?? []) as Variable[];
  const parentIds = new Set(
    variables.flatMap((variable) =>
      variable.parent_variable_id ? [variable.parent_variable_id] : [],
    ),
  );
  return variables.map((variable) => ({
    ...variable,
    has_children: parentIds.has(variable.variable_id),
  }));
}

type LinkedAnalysisRow = {
  analysis_id: string;
  analysis_name: string | null;
  analysis_setting: { display_name: string | null } | null;
};

export async function fetchLinkedAnalyses(): Promise<LinkedVariableOption[]> {
  const { data, error } = await analysisClient
    .from("analysis")
    .select(
      "analysis_id, analysis_name, analysis_status, analysis_setting(display_name)",
    )
    .neq("analysis_status", "template");

  if (error) throw error;

  return ((data ?? []) as LinkedAnalysisRow[])
    .map((row) => ({
      id: row.analysis_id,
      name:
        row.analysis_setting?.display_name?.trim() ||
        row.analysis_name ||
        row.analysis_id,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

type LinkedAnalysisStepRow = {
  analysis_step_id: string;
  analysis_step_name: string | null;
  analysis_order: number | null;
  analysisstep_setting: { display_name: string | null } | null;
};

export async function fetchLinkedAnalysisSteps(
  analysisId: string,
): Promise<LinkedVariableOption[]> {
  if (!analysisId) return [];

  const { data, error } = await analysisClient
    .from("analysisstep")
    .select(
      "analysis_step_id, analysis_step_name, analysis_order, analysisstep_setting(display_name)",
    )
    .eq("analysis_id", analysisId)
    .order("analysis_order", { ascending: true });

  if (error) throw error;

  return ((data ?? []) as LinkedAnalysisStepRow[]).map((row) => ({
    id: row.analysis_step_id,
    name:
      row.analysisstep_setting?.display_name?.trim() ||
      row.analysis_step_name ||
      row.analysis_step_id,
  }));
}
